import { z } from "zod";

/**
 * Sözleşme yorumu şemaları (CLOS-150 `[137]`).
 *
 * Yorum, değişmez bir versiyona (ve istenirse madde/paragrafa) kaynaklandırılır; ana
 * metni değiştirmez, onay/mutabakat/imza değildir. Varsayılan görünürlük EN DAR güvenli
 * bağlam — açık paylaşım (CLOS-153/154) olmadan başka organizasyona yayılmaz.
 */

/** Mention/bildirim kuralları sürümlü konfig (config/comment.json — CLOS-151 `[138]`). */
export const commentConfigSchema = z.object({
  version: z.number().int().positive(),
  mentionSearchLimit: z.number().int().positive(),
  mentionMaxPerComment: z.number().int().positive(),
  notificationBatchWindowMinutes: z.number().int().min(0),
  mentionRatePerMinute: z.number().int().positive(),
});
export type CommentConfig = z.infer<typeof commentConfigSchema>;

/** Şemadaki sınır MUTLAK tavan; gerçek sınır konfigten serviste uygulanır. */
const mentionListesi = z
  .array(z.uuid("Geçerli bir kullanıcı seçilmelidir"))
  .max(50, "Bir yorumda en fazla 50 kişi anılabilir")
  .optional();

const gövdeAlani = z
  .string()
  .trim()
  .min(1, "Yorum metni zorunludur")
  .max(5000, "Yorum en fazla 5000 karakter olabilir");

export const createContractCommentSchema = z.strictObject({
  versionId: z.uuid("Geçerli bir versiyon seçilmelidir"),
  targetClauseId: z.string().trim().min(1).optional(),
  targetBlockIndex: z
    .number()
    .int("Paragraf sırası tam sayı olmalıdır")
    .min(0, "Paragraf sırası negatif olamaz")
    .optional(),
  body: gövdeAlani,
  mentions: mentionListesi,
});
export type CreateContractCommentInput = z.infer<typeof createContractCommentSchema>;

export const updateContractCommentSchema = z.strictObject({
  body: gövdeAlani,
  mentions: mentionListesi,
});
export type UpdateContractCommentInput = z.infer<typeof updateContractCommentSchema>;

export const mentionCandidateQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
});

/** Mention önerisi — yalnız aktörün görmeye yetkili olduğu, kayda HÂLEN erişebilenler. */
export type MentionCandidateView = { userId: string; name: string };

/** Paylaşım komutu — alıcılar AÇIKÇA seçilir (CLOS-153 `[141]`). */
export const shareContractCommentSchema = z.strictObject({
  recipientTenantIds: z
    .array(z.uuid("Geçerli bir organizasyon seçilmelidir"))
    .min(1, "En az bir alıcı organizasyon seçilmelidir")
    .max(20, "Bir işlemde en fazla 20 alıcı seçilebilir"),
});
export type ShareContractCommentInput = z.infer<typeof shareContractCommentSchema>;

export const contractCommentListQuerySchema = z.object({
  versionId: z.uuid("Geçerli bir versiyon seçilmelidir").optional(),
});

export type ContractCommentStatus = "active" | "deleted";

/** Tartışma durumu (CLOS-152 `[139]`) — formal karar değil, zincir yönetimi. */
export type CommentResolutionStatus = "open" | "resolved";

export const commentResolutionSchema = z.strictObject({
  reason: z.string().trim().max(500, "Gerekçe en fazla 500 karakter olabilir").optional(),
});
export type CommentResolutionInput = z.infer<typeof commentResolutionSchema>;

export type ContractCommentView = {
  id: string;
  contractId: string;
  versionId: string;
  targetClauseId: string | null;
  targetBlockIndex: number | null;
  /** Tombstone: silinmiş yorumun metni okuma yolunda MASKELENİR. */
  body: string | null;
  visibilityScope: "workspace" | "shared";
  status: ContractCommentStatus;
  authorId: string;
  authorName: string;
  resolutionStatus: CommentResolutionStatus;
  resolvedByName: string | null;
  resolvedAt: string | null;
  editCount: number;
  editedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
};
